import React from 'react';
import {observer} from 'mobx-react-lite';
import Divider from '../../../subscription-plans/components/plan-info/divider/divider';
import FontBody1 from '../../../../components/fonts/font-body1/font-body1';
import FontHeading5 from '../../../../components/fonts/font-heading5/font-heading5';
import {FONT_VARIANT} from '../../../../components/fonts/font-props';
import {Button} from '../../../../components/inputs/button/button';
import {BUTTON_VARIANTS} from '../../../../components/inputs/button-variants';
import {Note, NOTE_TYPE} from '../../../../components/note/note';
import SbrMarginsFields, {ISbrMarginsFieldsProps} from './sbr-margins-fields';
import SbrMarginGroupCard from './sbr-margin-group-card';
import SbrMarginGroupsEditorStore from '../stores/sbr-margin-groups-editor-store';
import './sbr-margin-groups-section.scss';

export interface ISbrMarginGroupsSectionProps extends ISbrMarginsFieldsProps {
    marginGroupsStore: SbrMarginGroupsEditorStore;
    isLoadingOptions?: boolean;
    disabled?: boolean;
}

const SbrMarginGroupsSection: React.FC<ISbrMarginGroupsSectionProps> = observer(({
    marginGroupsStore,
    isLoadingOptions = false,
    disabled = false,
    validationGroup,
    ...planDefaultMarginsProps
}) => {
    const duplicateMessage = marginGroupsStore.getDuplicateSelectionsMessage();
    const hasGroups = marginGroupsStore.getGroups().length > 0;

    const addMarginGroupButton = (placement: 'empty' | 'footer') => (
        <div className={`sbr-margin-groups-section__add-button sbr-margin-groups-section__add-button--${placement}`}>
            <Button
                variant={BUTTON_VARIANTS.Primary}
                text="+ ADD MARGIN GROUP"
                onClick={marginGroupsStore.addGroup}
                disabled={disabled}
            />
        </div>
    );

    return (
        <div className="sbr-margin-groups-section">
            <FontHeading5 variant={FONT_VARIANT.BOLD}>SBR Default Margins</FontHeading5>

            <SbrMarginsFields
                {...planDefaultMarginsProps}
                disabled={disabled}
                validationGroup={validationGroup}
            />

            <Divider/>

            <FontHeading5 variant={FONT_VARIANT.BOLD}>Margin Groups</FontHeading5>

            {!hasGroups &&
                <div className="sbr-margin-groups-section__empty">
                    <span className="sbr-margin-groups-section__empty-text">
                        No margin groups configured. Default margins apply to all shipping options.
                    </span>
                    {addMarginGroupButton('empty')}
                </div>
            }

            {marginGroupsStore.getGroups().map((groupStore) => (
                <SbrMarginGroupCard
                    key={groupStore.clientId}
                    groupStore={groupStore}
                    marginGroupsStore={marginGroupsStore}
                    disabled={disabled}
                    isLoadingOptions={isLoadingOptions}
                    validationGroup={validationGroup}
                />
            ))}

            {duplicateMessage &&
                <div className="sbr-margin-groups-section__duplicates-note">
                    <Note
                        type={NOTE_TYPE.ERROR}
                        caption="Duplicate services"
                        body={<FontBody1>{duplicateMessage}</FontBody1>}
                    />
                </div>
            }

            {hasGroups && addMarginGroupButton('footer')}
        </div>
    );
});

export default SbrMarginGroupsSection;
